// src/components/FormInstituicao.js

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  TextField, 
  Button, 
  Grid, 
  CircularProgress, 
  Box, 
  FormControlLabel, 
  Switch, 
  MenuItem, 
  Typography, 
  Alert 
} from '@mui/material';
import useAuth from '../../hooks/useAuth';
import { Alert as MuiAlert } from '@mui/material'; // Importação adicional conforme solicitado

const FormInstituicao = ({ onSubmit, instituicaoInicial = {} }) => {
  const [instituicao, setInstituicao] = useState(instituicaoInicial);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Estados para Perfil de Gestão
  const [perfilGestao, setPerfilGestao] = useState({
    nome: '',
    username: '',
    email: '',
    senha: '',
    confirmSenha: ''
  });
  const [perfilError, setPerfilError] = useState('');
  
  const { user } = useAuth();

  const tiposInstituicao = [
    'Universidade',
    'Colégio',
    'Instituto Técnico'
  ];

  useEffect(() => {
    setInstituicao(instituicaoInicial);
    setLogoPreview(instituicaoInicial.logo || null);
    // Se estiver editando uma instituição existente, não precisa do perfil de gestão
    if (instituicaoInicial.id) {
      setPerfilGestao({
        nome: '',
        username: '',
        email: '',
        senha: '',
        confirmSenha: ''
      });
      setPerfilError('');
    }
  }, [instituicaoInicial]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInstituicao(prevState => ({ ...prevState, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setInstituicao(prevState => ({ ...prevState, [name]: checked }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setInstituicao(prevState => ({ ...prevState, logo: file }));
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  // Função para lidar com mudanças no perfil de gestão
  const handlePerfilChange = (e) => {
    const { name, value } = e.target;
    setPerfilGestao(prev => ({ ...prev, [name]: value }));
    setPerfilError('');
  };

  // Função para submeter o formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validação do perfil de gestão somente no cadastro inicial
    if (!instituicaoInicial.id) {
      if (!perfilGestao.nome || !perfilGestao.username || !perfilGestao.senha) {
        setPerfilError('Todos os campos do perfil de gestão são obrigatórios');
        setLoading(false);
        return;
      }
      if (perfilGestao.senha !== perfilGestao.confirmSenha) {
        setPerfilError('As senhas não coincidem');
        setLoading(false);
        return;
      }
      if (perfilGestao.senha.length < 8) {
        setPerfilError('A senha deve ter no mínimo 8 caracteres');
        setLoading(false);
        return;
      }
      const usernameRegex = /^[a-zA-Z0-9.]+$/;
      if (!usernameRegex.test(perfilGestao.username)) {
        setPerfilError('O username deve conter apenas letras, números e pontos');
        setLoading(false);
        return;
      }
    }

    try {
      const formData = new FormData();
      
      // Append dos campos da instituição
      Object.keys(instituicao).forEach(key => {
        if (key === 'logo' && instituicao[key] instanceof File) {
          formData.append(key, instituicao[key]);
        } else {
          formData.append(key, instituicao[key] || '');
        }
      });

      // Append dos dados do perfil de gestão somente no cadastro inicial
      if (!instituicaoInicial.id) {
        formData.append('perfilGestao', JSON.stringify({
          ...perfilGestao,
          tipo: 'GESTAO'
        }));
      }

      await onSubmit(formData);
    } catch (error) {
      console.error('Erro ao submeter o formulário:', error);
      // Opcional: Adicionar lógica para exibir erro ao usuário
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {/* Campos da Instituição */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nome"
            name="nome"
            value={instituicao.nome || ''}
            onChange={handleChange}
            required
            disabled={!!instituicaoInicial.nome}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={instituicao.email || ''}
            onChange={handleChange}
            required
            disabled={!!instituicaoInicial.email}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Endereço"
            name="endereco"
            value={instituicao.endereco || ''}
            onChange={handleChange}
            required
            helperText="Digite o endereço completo da instituição"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Telefone"
            name="telefone"
            value={instituicao.telefone || ''}
            onChange={handleChange}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Website"
            name="website"
            value={instituicao.website || ''}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Tipo de Instituição"
            name="tipoInstituicao"
            select
            value={instituicao.tipoInstituicao || 'Colégio'}
            onChange={handleChange}
            required
          >
            {tiposInstituicao.map((tipo) => (
              <MenuItem key={tipo} value={tipo}>
                {tipo}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Descrição"
            name="descricao"
            multiline
            rows={4}
            value={instituicao.descricao || ''}
            onChange={handleChange}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={instituicao.temTransportePublico || false}
                onChange={handleSwitchChange}
                name="temTransportePublico"
                color="primary"
              />
            }
            label="Tem Transporte Público"
          />
        </Grid>
        <Grid item xs={12}>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="logo-upload"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="logo-upload">
            <Button variant="contained" component="span">
              Upload Logo
            </Button>
          </label>
          {logoPreview && (
            <Box mt={2}>
              <img src={logoPreview} alt="Logo Preview" style={{ maxWidth: '200px' }} />
            </Box>
          )}
        </Grid>

        {/* Seção de Perfil de Gestão apenas no cadastro inicial */}
        {!instituicaoInicial.id && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Perfil de Gestão
              </Typography>
              {perfilError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {perfilError}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Gestor"
                name="nome"
                value={perfilGestao.nome}
                onChange={handlePerfilChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username do Gestor"
                name="username"
                value={perfilGestao.username}
                onChange={handlePerfilChange}
                required
                helperText="Use apenas letras, números e pontos"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email do Gestor"
                name="email"
                type="email"
                value={perfilGestao.email}
                onChange={handlePerfilChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Senha"
                name="senha"
                type="password"
                value={perfilGestao.senha}
                onChange={handlePerfilChange}
                required
                helperText="Mínimo 8 caracteres"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Confirmar Senha"
                name="confirmSenha"
                type="password"
                value={perfilGestao.confirmSenha}
                onChange={handlePerfilChange}
                required
              />
            </Grid>
          </>
        )}

        {/* Botão de Submissão */}
        <Grid item xs={12}>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

FormInstituicao.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  instituicaoInicial: PropTypes.shape({
    nome: PropTypes.string,
    email: PropTypes.string,
    endereco: PropTypes.string,
    telefone: PropTypes.string,
    website: PropTypes.string,
    descricao: PropTypes.string,
    logo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    tipoInstituicao: PropTypes.string,
    temTransportePublico: PropTypes.bool,
    id: PropTypes.number
  })
};

FormInstituicao.defaultProps = {
  instituicaoInicial: {}
};

export default FormInstituicao;
